import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineEnvelope,
  HiOutlineHeart,
  HiOutlineUser,
  HiOutlineArrowTrendingUp,
  HiOutlineArrowTrendingDown,
  HiOutlineArrowRight,
  HiOutlineQuestionMarkCircle,
  HiOutlineTicket,
  HiOutlineBanknotes,
  HiOutlineSparkles,
  HiOutlineTableCells,
  HiOutlineSun,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { useCurrentAdmin, hasPermission, hasFeature } from "./permissions";
import { useBrand } from "./brand";
import { dashboardAPI } from "../services/api";
import { useApi } from "./hooks/useApi";
import "./AdminDashboard.css";

/* Tiles are filtered twice: by what this product does at all (feature),
   and by what this person is allowed to see (permission). */
const STAT_META = [
  { key: "applications", label: "Applications", Icon: HiOutlineClipboardDocumentList, iconColor: "var(--admin-rust)", iconBg: "rgba(var(--admin-rust-rgb), 0.12)", permission: "applications.view", feature: "applications", to: "/admin/applications" },
  { key: "bookings", label: "Bookings", Icon: HiOutlineTicket, iconColor: "var(--admin-rust)", iconBg: "rgba(var(--admin-rust-rgb), 0.12)", permission: "blocks.view", feature: "bookings", to: "/admin/bookings", toReservations: "/admin/reservations" },
  { key: "bookedValue", label: "Booked Value", Icon: HiOutlineBanknotes, iconColor: "#0d9488", iconBg: "rgba(13,148,136,0.12)", permission: "blocks.view", feature: "bookings", to: "/admin/bookings", toReservations: "/admin/reservations?balance=owing" },
  { key: "workshopSignups", label: "Workshop Sign-ups", Icon: HiOutlineSparkles, iconColor: "#d97706", iconBg: "rgba(217,119,6,0.12)", permission: "workshops.view", feature: "workshops", to: "/admin/workshops" },
  { key: "subscribers", label: "Subscribers", Icon: HiOutlineEnvelope, iconColor: "#16a34a", iconBg: "rgba(22,163,74,0.12)", permission: "subscribers.view", feature: "subscribers", to: "/admin/subscribers" },
  { key: "donations", label: "Donations", Icon: HiOutlineHeart, iconColor: "#7c3aed", iconBg: "rgba(124,58,237,0.12)", permission: "donations.view", feature: "donations", to: "/admin/donations" },
  { key: "pendingReviews", label: "Pending Reviews", Icon: HiOutlineUser, iconColor: "#d97706", iconBg: "rgba(217,119,6,0.12)", permission: "applications.view", feature: "applications", to: "/admin/applications?status=New" },
];

const ACTIVITY_ICONS = {
  Application: HiOutlineClipboardDocumentList,
  Subscriber: HiOutlineEnvelope,
  Donation: HiOutlineHeart,
};
const ACTIVITY_META = {
  Application: { bg: "rgba(var(--admin-rust-rgb), 0.12)", color: "#e05a2b" },
  Subscriber: { bg: "rgba(22,163,74,0.12)", color: "#16a34a" },
  Donation: { bg: "rgba(124,58,237,0.12)", color: "#7c3aed" },
};
const QUICK_LINKS = [
  {
    label: "View Applications",
    sub: "Review and manage applications",
    to: "/admin/applications",
    Icon: HiOutlineClipboardDocumentList,
    bg: "rgba(var(--admin-rust-rgb), 0.1)",
    color: "#e05a2b",
    permission: "applications.view",
    feature: "applications",
  },
  { label: "Reservations", sub: "Every booking, its guests and payments", to: "/admin/reservations", Icon: HiOutlineTicket, bg: "rgba(var(--admin-rust-rgb), 0.1)", color: "var(--admin-rust)", permission: "reservations.view", feature: "reservations" },
  { label: "Booking Calendar", sub: "Every room and bed, night by night", to: "/admin/calendar", Icon: HiOutlineTableCells, bg: "rgba(13,148,136,0.1)", color: "#0d9488", permission: "inventory.view", feature: "reservations" },
  { label: "Retreat Schedule", sub: "Blocks, dated retreats and waiting lists", to: "/admin/retreats", Icon: HiOutlineSun, bg: "rgba(217,119,6,0.1)", color: "#d97706", permission: "blocks.view", feature: "reservations" },
  { label: "View Bookings", sub: "Who's booked which retreat", to: "/admin/bookings", Icon: HiOutlineTicket, bg: "rgba(var(--admin-rust-rgb), 0.1)", color: "var(--admin-rust)", permission: "blocks.view", feature: "bookings", unless: "reservations" },
  { label: "View Workshops", sub: "Dates, sign-ups and waiting lists", to: "/admin/workshops", Icon: HiOutlineSparkles, bg: "rgba(217,119,6,0.1)", color: "#d97706", permission: "workshops.view", feature: "workshops" },
  { label: "View Subscribers", sub: "See all email subscribers", to: "/admin/subscribers", Icon: HiOutlineEnvelope, bg: "rgba(22,163,74,0.1)", color: "#16a34a", permission: "subscribers.view", feature: "subscribers" },
  { label: "View Donations", sub: "See all donations and contributors", to: "/admin/donations", Icon: HiOutlineHeart, bg: "rgba(124,58,237,0.1)", color: "#7c3aed", permission: "donations.view", feature: "donations" },
];

const PERIODS = ["Last 7 Days", "Last 30 Days", "Last 90 Days"];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="adb__tooltip">
      <p className="adb__tooltip-label">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="adb__tooltip-val">
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

function Skeleton({ h = "1rem", w = "100%" }) {
  return <div style={{ height: h, width: w, background: "#e8e3da", borderRadius: "4px", animation: "pulse 1.5s ease-in-out infinite" }} />;
}

/* "5 min ago", "3 h ago", "Oct 22": the server's wall-clock time, read as local. */
const whenAgo = (t) => {
  const m = String(t || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return "";
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/* Compute % change between current and previous value */
const pctChange = (curr, prev) => {
  if (!prev || prev === 0) return null;
  const diff = ((curr - prev) / prev) * 100;
  return { value: Math.abs(diff).toFixed(1), up: diff >= 0 };
};

export default function AdminDashboard() {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const brand = useBrand();
  const [period, setPeriod] = useState("Last 30 Days");
  const [periodOpen, setPeriodOpen] = useState(false);

  // While permissions are still loading, don't hide anything yet — avoids a flash
  // of "empty dashboard" before the /auth/me call resolves (usually <200ms).
  const canViewApplications = permLoading
    ? true
    : hasFeature(admin, "applications") && hasPermission(admin, "applications.view");
  const canViewNotifications = permLoading ? true : hasPermission(admin, "notifications.view");

  const visibleStats = permLoading
    ? STAT_META.filter((m) => m.feature === "subscribers")
    : STAT_META.filter((m) => hasFeature(admin, m.feature) && hasPermission(admin, m.permission));
  const runsReservations = !!admin?.features?.reservations;
  const visibleLinks = permLoading
    ? []
    : QUICK_LINKS.filter((q) => hasFeature(admin, q.feature) && hasPermission(admin, q.permission) && !(q.unless && admin?.features?.[q.unless]));

  // Retreat filter (V2.1): only offered to staff who can see retreats; the API decides what each person gets.
  const [pathwayId, setPathwayId] = useState("");
  const { data: dashData, loading } = useApi(() => dashboardAPI.get(pathwayId ? { pathway_id: pathwayId } : {}), [pathwayId]);
  const attention = dashData?.attention || [];
  // The bell links to #attention: bring it into view once it's there.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== "#attention" || !dashData) return;
    const el = document.getElementById("attention");
    if (el) { el.scrollIntoView({ block: "start" }); el.focus({ preventScroll: true }); }
  }, [hash, dashData]);
  const retreats = dashData?.retreats || [];

  const kpis = dashData?.kpis || {};
  const statLink = (m) => (runsReservations && m.toReservations ? `${m.toReservations}${pathwayId ? `${m.toReservations.includes("?") ? "&" : "?"}pathway_id=${pathwayId}` : ""}` : m.to);
  const prevKpis = dashData?.prevKpis || {};
  const chartData = dashData?.chart || [];
  const activity = dashData?.activity || [];

  /* Real trend % from API (curr vs prev period), fall back to null */
  const trends = {
    applications: pctChange(kpis.applications, prevKpis.applications),
    subscribers: pctChange(kpis.subscribers, prevKpis.subscribers),
    donations: pctChange(kpis.donations, prevKpis.donations),
    pendingReviews: pctChange(kpis.pending_reviews, prevKpis.pending_reviews),
  };

  const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
  const STATS = [
    { key: "applications", value: kpis.applications ?? "—" },
    { key: "bookings", value: kpis.bookings ?? "—" },
    { key: "bookedValue", value: kpis.booked_value != null ? money(kpis.booked_value) : "—" },
    { key: "workshopSignups", value: kpis.workshop_signups ?? "—" },
    { key: "subscribers", value: kpis.subscribers ?? "—" },
    { key: "donations", value: kpis.donations != null ? money(kpis.donations) : "—" },
    { key: "pendingReviews", value: kpis.pending_reviews ?? "—" },
  ];

  const APP_SUMMARY = {
    new: kpis.new_applications ?? 0,
    reviewing: kpis.reviewing ?? 0,
    accepted: kpis.accepted ?? 0,
    rejected: kpis.rejected ?? 0,
  };

  return (
    <AdminLayout pageTitle="Dashboard" pageSubtitle={`Welcome back. Here's what's happening with ${brand.short}.`}>
      {retreats.length > 0 && (
        <div className="adb__scope">
          <label htmlFor="adb-scope">Figures for</label>
          <select id="adb-scope" value={pathwayId} onChange={(e) => setPathwayId(e.target.value)}>
            <option value="">All retreats</option>
            {retreats.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.start_date}</option>)}
          </select>
          {pathwayId && <button type="button" className="a-btn a-btn--outline" onClick={() => setPathwayId("")}>Show all</button>}
        </div>
      )}
      {/* KPI Cards: each opens the page behind its number */}
      <div className="adb__stats-grid">
        {visibleStats.map((m) => {
          const i = STAT_META.findIndex((sm) => sm.key === m.key);
          const s = STATS[i] || {};
          const trend = trends[m.key];
          return (
            <Link key={m.key} to={statLink(m)} className="acard adb__stat-card adb__stat-card--link" aria-label={`${m.label}: ${loading ? "loading" : s.value}. Open ${m.label.toLowerCase()}`}>
              <div className="adb__stat-top">
                <div className="adb__stat-icon-wrap" style={{ background: m.iconBg }}>
                  <m.Icon style={{ color: m.iconColor, fontSize: "1.2rem" }} />
                </div>
                <p className="adb__stat-label">{m.label}</p>
              </div>
              {loading ? <Skeleton h="2rem" w="60%" /> : <p className="adb__stat-value">{s.value}</p>}
              {m.key === "bookings" && runsReservations ? (
                <p className="adb__stat-change" style={{ color: "var(--admin-muted)" }}>
                  {kpis.bookings_confirmed ?? 0} confirmed, {Math.max(0, (kpis.bookings ?? 0) - (kpis.bookings_confirmed ?? 0))} held{kpis.earlier_bookings ? ` · plus ${kpis.earlier_bookings} earlier` : ""}
                </p>
              ) : m.key === "bookedValue" && runsReservations ? (
                <p className="adb__stat-change" style={{ color: "var(--admin-muted)" }}>Confirmed and held reservations{dashData?.scope ? ` · ${dashData.scope.name}` : ""}</p>
              ) : trend ? (
                <p className={`adb__stat-change ${trend.up ? "up" : "down"}`}>
                  {trend.up ? <HiOutlineArrowTrendingUp /> : <HiOutlineArrowTrendingDown />}
                  {trend.value}% vs last period
                </p>
              ) : (
                <p className="adb__stat-change" style={{ color: "var(--admin-muted)" }}>
                  No comparison data yet
                </p>
              )}
            </Link>
          );
        })}
      </div>

      {/* Needs attention: each count is the same list its link opens */}
      {!permLoading && (attention.length > 0 || runsReservations) && (
        <div id="attention" className="acard adb__attention" tabIndex={-1}>
          <h3 className="adb__section-title">Needs attention{dashData?.scope ? ` · ${dashData.scope.name}` : ""}</h3>
          {loading && !dashData ? <Skeleton h="1.2rem" w="50%" /> : attention.length === 0 ? (
            <p className="adb__attention-none"><HiOutlineCheckCircle aria-hidden="true" /> Nothing is waiting on anyone right now.</p>
          ) : (
            <ul className="adb__attention-list">
              {attention.map((a) => (
                <li key={a.key}>
                  <Link to={`${a.link}${pathwayId && a.link.startsWith("/admin/reservations") ? `&pathway_id=${pathwayId}` : ""}`} className={`adb__attention-item adb__attention-item--${a.tone}`}>
                    <HiOutlineExclamationTriangle aria-hidden="true" />
                    <b>{a.count}</b> <span>{a.label}</span>
                    <HiOutlineArrowRight className="adb__quick-arrow" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Main grid */}
      <div className={`adb__main-grid ${canViewApplications ? "" : "adb__main-grid--single"}`}>
        {/* Chart — only for admins who can view applications */}
        {canViewApplications && (
          <div className="acard adb__chart-card">
            <div className="adb__chart-head">
              <h3 className="adb__section-title">Applications Overview</h3>
              <div className="adb__period-wrap">
                <button type="button" className="adb__period-btn" onClick={() => setPeriodOpen((v) => !v)}>
                  {period} <HiOutlineArrowTrendingUp />
                </button>
                {periodOpen && (
                  <div className="adb__period-dropdown">
                    {PERIODS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`adb__period-opt ${p === period ? "active" : ""}`}
                        onClick={() => {
                          setPeriod(p);
                          setPeriodOpen(false);
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="adb__chart-wrap">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(27,27,27,0.06)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b6258" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b6258" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "12px" }} />
                  <Line type="monotone" dataKey="new" name="New" stroke="#e05a2b" strokeWidth={2.5} dot={{ r: 3, fill: "#e05a2b" }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="reviewing" name="Reviewing" stroke="#d97706" strokeWidth={2} dot={{ r: 3, fill: "#d97706" }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="accepted" name="Accepted" stroke="#16a34a" strokeWidth={2} dot={{ r: 3, fill: "#16a34a" }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="rejected" name="Rejected" stroke="#dc2626" strokeWidth={2} dot={{ r: 3, fill: "#dc2626" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="adb__app-summary">
              {[
                { label: "New", count: APP_SUMMARY.new, color: "#e05a2b" },
                { label: "Reviewing", count: APP_SUMMARY.reviewing, color: "#d97706" },
                { label: "Accepted", count: APP_SUMMARY.accepted, color: "#16a34a" },
                { label: "Rejected", count: APP_SUMMARY.rejected, color: "#dc2626" },
              ].map((s) => (
                <div key={s.label} className="adb__summary-item">
                  <span className="adb__summary-dot" style={{ background: s.color }} />
                  <span className="adb__summary-label">{s.label}</span>
                  <span className="adb__summary-count">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity — only items the admin has permission to view */}
        <div className="acard adb__activity-card">
          <h3 className="adb__section-title">Recent Activity</h3>
          <div className="adb__activity-list">
            {(() => {
              if (activity.length === 0 && !loading) {
                return <p style={{ textAlign: "center", color: "var(--admin-muted)", padding: "2rem 0", fontSize: "0.85rem" }}>No recent activity yet.</p>;
              }
              return activity.map((a) => {
                const type = ACTIVITY_META[a.type] ? a.type : null;
                const Icon = (type && ACTIVITY_ICONS[type]) || (a.type === "reservation" ? HiOutlineTicket : a.type === "stay" || a.type === "property_hold" ? HiOutlineTableCells : HiOutlineClipboardDocumentList);
                const meta = (type && ACTIVITY_META[type]) || { bg: "rgba(var(--admin-rust-rgb), 0.1)", color: "var(--admin-rust)" };
                return (
                  <Link key={a.id} to={a.link} className="adb__activity-item adb__activity-item--link">
                    <div className="adb__activity-icon" style={{ background: meta.bg }}>
                      <Icon style={{ color: meta.color }} />
                    </div>
                    <p className="adb__activity-text">{a.message}{a.actor ? <span className="adb__activity-who"> · {a.actor}</span> : null}</p>
                    <time className="adb__activity-time" dateTime={a.time} title={a.time}>{whenAgo(a.time)}</time>
                  </Link>
                );
              });
            })()}
          </div>
          {canViewNotifications && !runsReservations && (
            <Link to="/admin/notifications" className="adb__view-all a-btn a-btn--outline">
              View all activity
            </Link>
          )}
        </div>
      </div>

      {/* Bottom grid */}
      <div className="adb__bottom-grid">
        {visibleLinks.length > 0 && (
          <div className="acard adb__quick-card">
            <h3 className="adb__section-title">Quick Links</h3>
            <div className="adb__quick-grid">
              {visibleLinks.map((q) => (
                <Link key={q.label} to={q.to} className="adb__quick-item">
                  <div className="adb__quick-icon" style={{ background: q.bg }}>
                    <q.Icon style={{ color: q.color, fontSize: "1.2rem" }} />
                  </div>
                  <div className="adb__quick-text">
                    <p className="adb__quick-label">{q.label}</p>
                    <p className="adb__quick-sub">{q.sub}</p>
                  </div>
                  <HiOutlineArrowRight className="adb__quick-arrow" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Need Help — real email CTA */}
        <div className="acard adb__help-card">
          <h3 className="adb__section-title">Need Help?</h3>
          <div className="adb__help-body">
            <div className="adb__help-icon">
              <HiOutlineQuestionMarkCircle />
            </div>
            <div>
              <p className="adb__help-text">If you have any questions or need assistance with the admin panel, reach out to the development team.</p>
              <a href="mailto:dev@forge.ngo?subject=Admin Panel Support" className="a-btn a-btn--rust adb__help-btn" style={{ marginTop: "0.75rem", display: "inline-flex" }}>
                <HiOutlineEnvelope /> Email the dev team
              </a>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
